import { useEffect, useRef, useState } from 'react';
import {
    Text,
    Box,
    Flex,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalCloseButton,
    ModalBody,
    ModalFooter,
    Button,
    useToast,
    Input,
} from '@chakra-ui/react';
import { TGetAllClientProductsPayload, useUpdateTagSplitMutation } from 'core/API/productsApi';
import { useTranslation } from 'helpers/languages/translations';
import { MAX_NAME_SPLITS } from '../consts';

type TTagSplitModalProps = {
    isTagOpen: boolean;
    OnTagClose: () => void;
    clientProducts?: TGetAllClientProductsPayload;
    readonly: boolean;
};

const TagSplitModal = ({ clientProducts, isTagOpen, readonly, OnTagClose }: TTagSplitModalProps) => {
    const translate = useTranslation();
    const toast = useToast();

    const [updateTagSplit, { isLoading: isTagSplitLoading }] = useUpdateTagSplitMutation();

    const [nameSplitTags, setNameSplitTags] = useState<string[]>([]);
    const [nameSplitTagWeights, setNameSplitTagWeights] = useState<(number | null)[]>([]);
    const tags = clientProducts?.products?.[0]?.tag_split || [];
    const tagWeights = clientProducts?.products?.[0]?.tag_weights || [];

    useEffect(() => {
        !isTagOpen && tags.length && setNameSplitTags(tags);
        !isTagOpen && tagWeights.length && setNameSplitTagWeights(tagWeights);
    }, [isTagOpen, tags]);

    const handleTagChange = async () => {
        const tagWeights = [...nameSplitTagWeights];
        const tagWeightsEmpty = tagWeights.every((wt) => !wt);

        while (tagWeights.length && !tagWeights.at(-1)) {
            tagWeights.pop();
        }

        const tagsToSave = [...nameSplitTags];
        tagsToSave.forEach((tag, i) => {
            tagsToSave[i] = tag.trim();
        });
        while (tagsToSave.length && !tagsToSave.at(-1)) {
            tagsToSave.pop();
        }

        const tagsEmpty = tagsToSave.length === 0;

        const resp = await updateTagSplit({
            tags: tagsEmpty ? [] : tagsToSave,
            tagWeights: tagWeightsEmpty ? [] : tagWeights,
        });
        const data = (resp as any).data || (resp as any).error.data;

        const success = data.code === 200;

        toast({
            title: translate(`product.name_split.tag.${success ? 'success' : 'error'}`),
            status: success ? 'success' : 'error',
        });

        OnTagClose();
    };

    return (
        <Modal isCentered isOpen={isTagOpen} onClose={OnTagClose} size="xl">
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>
                    <Text as="h3">{translate('splits.fac.header')}</Text>
                </ModalHeader>
                <ModalCloseButton mt="2" isDisabled={isTagSplitLoading} />
                <ModalBody fontSize="sm" maxH="500px" overflow="scroll">
                    {Array(MAX_NAME_SPLITS)
                        .fill('')
                        .map((split, i) => (
                            <Flex mb={3} alignItems="center" key={`split_${i}`}>
                                <Text>{translate('product.name_split_i', { idx: i + 1 })}</Text>
                                <Input
                                    ml={3}
                                    flex={2}
                                    size="sm"
                                    value={nameSplitTags[i]}
                                    placeholder={translate('product.name_split_i', { idx: i + 1 }) as string}
                                    onChange={(e) =>
                                        setNameSplitTags((tags) => {
                                            const newTags = [...tags];
                                            newTags[i] = e.target.value;
                                            return newTags.map((nt) => (!nt ? '' : nt));
                                        })
                                    }
                                    isDisabled={readonly}
                                />
                                <Input
                                    ml={2}
                                    flex={1}
                                    size="sm"
                                    type="number"
                                    min={1}
                                    max={5}
                                    onKeyDown={(e) => {
                                        if (e.key === '.' || e.key?.toLowerCase() === 'e' || e.key === '-') {
                                            e.preventDefault();
                                        }
                                    }}
                                    value={nameSplitTagWeights[i] as number}
                                    placeholder={translate('splits.modal.weight.placeholder') as string}
                                    onChange={(e) => {
                                        setNameSplitTagWeights((weights) => {
                                            const newWeights = [...weights];
                                            newWeights[i] = parseInt(e.target.value, 10);
                                            const weightsToReturn = newWeights.map((nw) => {
                                                return !nw ? null : nw < 1 ? 1 : nw > 5 ? 5 : Math.floor(nw);
                                            });
                                            if (weightsToReturn.every((wt) => !wt)) {
                                                return [];
                                            }
                                            return weightsToReturn;
                                        });
                                    }}
                                    isDisabled={readonly}
                                />
                            </Flex>
                        ))}
                </ModalBody>

                <ModalFooter>
                    <Button isDisabled={isTagSplitLoading} variant="outline" size="sm" mr={3} onClick={OnTagClose}>
                        {translate('splits.fac.cancel')}
                    </Button>
                    {!readonly && (
                        <Button
                            onClick={handleTagChange}
                            colorScheme="blue"
                            bg="blueMain"
                            size="sm"
                            isLoading={isTagSplitLoading}
                        >
                            {translate('splits.fac.confirm')}
                        </Button>
                    )}
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default TagSplitModal;
